import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eatsKeys, productApi, type AvailabilityWindow } from '@noor/eats-api';
import { menuKeys } from '@/entities/menu';
import {
  diffBranchProducts,
  type BranchAvailability,
  type BranchProductsDiff,
} from '../lib/branch-products';
import type { BranchProduct } from './schema';

export interface SyncBranchProductsVars {
  /** The branch the products are being attached to. */
  branchId: string;
  /** The rows the merchant left in step 2. */
  rows: BranchProduct[];
  /** What the branch stocked when the editor opened (empty for a create). */
  seeded: BranchProduct[];
}

/** How many of the writes failed, so a partial sync can be reported honestly. */
export interface SyncBranchProductsResult {
  failed: number;
  firstError?: unknown;
}

/**
 * Write step 2's product list onto the branch.
 *
 * The Product Service owns this relation from the PRODUCT's side — every route
 * is `/products/{id}/restaurant-products` — so a branch stocking N products is
 * N requests, not one. There is no branch-side bulk endpoint to replace them
 * with; `patches` batches BRANCHES for one product, which is the opposite axis.
 *
 * The writes are issued CONCURRENTLY and settled rather than raced: one
 * product failing must not abandon the rest, since each is an independent row
 * the merchant can see. The count of failures comes back so the caller can say
 * the branch saved while some products did not — the same shape as the photo
 * reporting in {@link useUpsertBranch}.
 */
export function useSyncBranchProducts() {
  const queryClient = useQueryClient();

  return useMutation<SyncBranchProductsResult, Error, SyncBranchProductsVars>({
    meta: { suppressErrorToast: true },
    mutationFn: async ({ branchId, rows, seeded }) => {
      const diff = diffBranchProducts(rows, seeded);
      const results = await Promise.allSettled(toRequests(branchId, diff));
      const rejected = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      return {
        failed: rejected.length,
        ...(rejected[0] ? { firstError: rejected[0].reason } : {}),
      };
    },
    onSuccess: (_result, { branchId }) => {
      // Both the branch's menu and the merchant-wide catalog can have changed:
      // detaching a product from its LAST branch deletes the product outright.
      void queryClient.invalidateQueries({ queryKey: menuKeys.branchProducts(branchId) });
      void queryClient.invalidateQueries({ queryKey: menuKeys.all });
      void queryClient.invalidateQueries({ queryKey: eatsKeys.restaurants.all });
    },
  });
}

/** The branch's hours as the wire's `availability_window`. */
function toAvailabilityWindow(availability: BranchAvailability): AvailabilityWindow {
  return {
    ...(availability.from ? { available_from: availability.from } : {}),
    ...(availability.to ? { available_to: availability.to } : {}),
  };
}

/** The diff as one flat list of in-flight requests. */
function toRequests(branchId: string, diff: BranchProductsDiff): Promise<unknown>[] {
  return [
    ...diff.attach.map(({ productId, price, stockQuantity, availability }) =>
      productApi.addRestaurantProducts(productId, [
        {
          restaurant_id: branchId,
          price,
          ...(stockQuantity === undefined ? {} : { stock_quantity: stockQuantity }),
          ...(availability === undefined
            ? {}
            : { availability_window: toAvailabilityWindow(availability) }),
        },
      ]),
    ),
    ...diff.reprice.map(({ productId, price, stockQuantity, availability }) =>
      productApi.updateRestaurantProducts(productId, [
        {
          restaurant_id: branchId,
          ...(price === undefined ? {} : { price }),
          ...(availability === undefined
            ? {}
            : {
                availability_window:
                  availability === null ? null : toAvailabilityWindow(availability),
              }),
          ...(stockQuantity === undefined
            ? {}
            : stockQuantity === null
              ? { stop_counting: true }
              : { stock_quantity: stockQuantity }),
        },
      ]),
    ),
    ...diff.detach.map((productId) =>
      productApi.removeFromRestaurants(productId, [branchId]),
    ),
  ];
}
